import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useProjectFixQueue } from '_/hooks/useProjects';
import { formatRelativeTime } from '_/utils/dateUtils';
import zeusSdk from '_/utils/SDKs';
import { Table, message, Button, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { ReactElement, useEffect, useCallback } from 'react';

interface IFixQueueItem {
  id: string;
  collectionSlug: string;
  collectionName: string;
  sourceSlug: string;
  sourceName: string;
  stoppedAt: string;
}

function FixQueue(): ReactElement {
  const { setBreadcrumbs, setOnRefresh, setRefreshLoading } = useTopHeader();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();

  const { data: fixqueue = [], isLoading, isRefetching, refetch } = useProjectFixQueue(selectedProject?.id);

  const handleStartFixing = async (snapshotId: string) => {
    try {
      await zeusSdk.snapshots.assignToMe(snapshotId);
      message.success('Snapshot assigned to you');
      void refetch();
    } catch {
      message.error('Failed to assign snapshot');
    }
  };

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetching);

    return () => {
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [setOnRefresh, setRefreshLoading, isRefetching, handleRefresh]);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      { title: 'Fix Queue' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization, selectedOrgSlug, selectedProject, selectedProjectSlug]);

  const columns: ColumnsType<IFixQueueItem> = [
    {
      title: 'Collection',
      dataIndex: 'collectionName',
      key: 'collectionName',
      render: (name: string, record: IFixQueueItem) => (
        <a href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}`}>{name}</a>
      ),
      sorter: (a: IFixQueueItem, b: IFixQueueItem) => a.collectionName.localeCompare(b.collectionName),
    },
    {
      title: 'Source',
      dataIndex: 'sourceName',
      key: 'sourceName',
      render: (name: string, record: IFixQueueItem) => (
        <a
          href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}/sources/${record.sourceSlug}`}
        >
          {name}
        </a>
      ),
      sorter: (a: IFixQueueItem, b: IFixQueueItem) => a.sourceName.localeCompare(b.sourceName),
    },
    {
      title: 'Lag',
      dataIndex: 'stoppedAt',
      key: 'stoppedAt',
      render: (stoppedAt: string) => formatRelativeTime(stoppedAt),
      sorter: (a: IFixQueueItem, b: IFixQueueItem) => new Date(a.stoppedAt).getTime() - new Date(b.stoppedAt).getTime(),
    },
    {
      title: '',
      key: 'actions',
      render: (_: any, record: IFixQueueItem) => (
        <Space>
          <Button type="primary" size="small" onClick={() => void handleStartFixing(record.id)}>
            Start Fixing
          </Button>
          <a
            href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}/sources/${record.sourceSlug}/snapshots/${record.id}`}
            target="_blank"
            rel="noreferrer"
          >
            <Button size="small">View</Button>
          </a>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <p>These are the current {fixqueue.length} sources with unassigned snapshots that require fixing.</p>
      <Table
        dataSource={fixqueue as IFixQueueItem[]}
        columns={columns}
        rowKey={(record: IFixQueueItem) => record.id}
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} items`,
        }}
      />
    </div>
  );
}

export default FixQueue;
